'use client';

/// <reference path="../../../types/global.d.ts" />
import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FiGithub, FiLinkedin, FiTwitter, FiMail } from 'react-icons/fi';
import { IoHeart } from 'react-icons/io5';
import Image from 'next/image';
import type { IconType } from 'react-icons';
import { SocialLink } from '../../';
import { footerMock } from '../../../store/mocks';
import type { FooterSocial } from '../../../store/mocks';
import styles from './footer.module.scss';

gsap.registerPlugin(ScrollTrigger);

const FOOTER_SOCIAL_ICONS: Record<FooterSocial['id'], IconType> = {
    github: FiGithub,
    linkedin: FiLinkedin,
    twitter: FiTwitter,
    email: FiMail,
};

export default function Footer() {
    const { logoSrc, logoText, logoAlt, tagline, socials, copyright, heartLabel } = footerMock;
    const sectionRef = useRef<HTMLElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.fromTo(
                innerRef.current,
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 0.8, scrollTrigger: { trigger: innerRef.current, start: 'top 90%' } },
            );
        }, sectionRef);
        return () => ctx.revert();
    }, []);

    return (
        <footer id="contact" ref={sectionRef} className={styles.footer}>
            <div className={styles.container}>
                <div ref={innerRef} className={styles.inner}>
                    <div className={styles.logoBlock}>
                        <div className={styles.logoIcon}>
                            <Image src={logoSrc} alt={logoAlt} width={48} height={48} />
                        </div>
                        <span className={styles.logoText}>{logoText}</span>
                    </div>
                    <p className={styles.tagline}>{tagline}</p>
                    <div className={styles.socials}>
                        {socials.map(s => (
                            <SocialLink
                                key={s.id}
                                href={s.href}
                                icon={FOOTER_SOCIAL_ICONS[s.id]}
                                label={s.label}
                                size={20}
                                className={styles.socialLink}
                            />
                        ))}
                    </div>
                    <div className={styles.divider} />
                    <p className={styles.copyright}>
                        {copyright} <IoHeart className={styles.heart} aria-label={heartLabel} /> and code
                    </p>
                </div>
            </div>
        </footer>
    );
}
